import { useEffect, useState } from 'react';

export type FormSignals = {
  /** Whether any text input on the page is currently focused. */
  isTyping: boolean;
  /** Length of the current password field value (0 if none). */
  passwordLength: number;
  /** Whether the password field is currently revealed as plain text. */
  showPassword: boolean;
};

const readSignals = (): FormSignals => {
  const active = document.activeElement;
  const isTyping =
    active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;

  // Logto password fields keep `autocomplete="current-password" | "new-password"`
  // even after the show/hide toggle flips their `type` between password and text.
  const passwordField = document.querySelector<HTMLInputElement>(
    'input[autocomplete*="password" i]'
  );

  return {
    isTyping,
    passwordLength: passwordField?.value.length ?? 0,
    showPassword: passwordField?.type === 'text',
  };
};

/**
 * Derives sign-in form interaction state (typing, password length, password visibility)
 * from global DOM events, so the decorative cartoon panel can react to Logto's real forms
 * without coupling to any individual form component.
 */
const useFormSignals = (): FormSignals => {
  const [signals, setSignals] = useState<FormSignals>({
    isTyping: false,
    passwordLength: 0,
    showPassword: false,
  });

  useEffect(() => {
    const update = () => {
      setSignals((previous) => {
        const next = readSignals();
        if (
          previous.isTyping === next.isTyping &&
          previous.passwordLength === next.passwordLength &&
          previous.showPassword === next.showPassword
        ) {
          return previous;
        }
        return next;
      });
    };

    update();

    const events: Array<keyof DocumentEventMap> = [
      'input',
      'focusin',
      'focusout',
      'click',
      'keyup',
      'change',
    ];
    for (const event of events) {
      document.addEventListener(event, update, true);
    }

    // Watch for the show/hide password toggle, which changes the input `type` attribute.
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['type'],
    });

    return () => {
      for (const event of events) {
        document.removeEventListener(event, update, true);
      }
      observer.disconnect();
    };
  }, []);

  return signals;
};

export default useFormSignals;
